import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";
import type { CreateThreadInput } from "@cleo/client-runtime/operations";
import {
  ROOM_ID_PATTERN,
  type EnvironmentId,
  type MessageId,
  type ModelSelection,
  type ThreadId,
  type WorldRoomEntry,
  type RuntimeMode,
  type ScopedSettingsSource,
} from "@cleo/contracts";

import { prepareRootSessionLaunch } from "../rootSessionBirth";
import {
  normalizeWorldSessionDoorRefusalKind,
  type WorldContextBirthClaim,
  type WorldRoomEntryOutcome,
  type WorldSessionDoorRefusalKind,
} from "./worldSessionClient";
import { boardTitles } from "./boardRegistryClient";
import {
  type WorldSessionLaunchProject,
  resolveWorldSessionRuntimeMode,
} from "./worldSessionFirstExecution";

export type WorldSessionExecutionBirthRefusal = {
  readonly _tag: "Refused";
  readonly reason:
    | "source-execution-required"
    | "source-coordinate-mismatch"
    | "world-workspace-required"
    | "world-files-root-required"
    | "world-provider-required"
    | "invalid-coordinate"
    | "empty-label"
    | "board-title-reserved";
};

export interface PreparedWorldSessionExecutionBirth {
  readonly _tag: "Launch";
  readonly environmentId: EnvironmentId;
  readonly input: CreateThreadInput;
}

/**
 * Root birth has one parent: the World. Its workspace is supplied only by
 * the World's durable device binding; there is deliberately no execution
 * parameter for an active session to leak through.
 */
export function prepareWorldRootSessionExecutionBirth(input: {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly label: string;
  readonly project: WorldSessionLaunchProject | null;
  readonly modelSelection?: ModelSelection;
  readonly filesRoot: string | null;
  /** SB3: the launching workroom; absent births a plain session. */
  readonly workroomKey?: string | null;
  /** UP7: the room, an explicit mode, and the settings the override record lives in. */
  readonly roomId?: string | null | undefined;
  readonly runtimeMode?: RuntimeMode | undefined;
  readonly settings?: ScopedSettingsSource | undefined;
}): PreparedWorldSessionExecutionBirth | WorldSessionExecutionBirthRefusal {
  if (!input.worldKey.trim() || !input.sessionId.trim()) {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  if (!input.label.trim()) return { _tag: "Refused", reason: "empty-label" };
  if (input.workroomKey == null && boardTitles(input.worldKey).includes(input.label.trim())) {
    return { _tag: "Refused", reason: "board-title-reserved" };
  }
  if (input.project === null) {
    return { _tag: "Refused", reason: "world-workspace-required" };
  }
  if (input.project.modelSelection === null && input.modelSelection === undefined) {
    return { _tag: "Refused", reason: "world-provider-required" };
  }
  const prepared = prepareRootSessionLaunch({
    mode: "world",
    activeWorldKey: input.worldKey,
    activeSessionId: input.sessionId,
    projectId: input.project.projectId,
    ask: input.label,
    now: new Date().toISOString(),
    modelSelection: input.modelSelection ?? input.project.modelSelection!,
    runtimeMode: resolveWorldSessionRuntimeMode(input),
    interactionMode: "default",
    branch: null,
    worktreePath: null,
    ...(input.workroomKey != null ? { workroomKey: input.workroomKey } : {}),
  });
  if (prepared._tag === "Refused") {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  return {
    _tag: "Launch",
    environmentId: input.project.environmentId,
    input: prepared.input,
  };
}

/**
 * Nested birth has one parent: the addressed session. Every execution field
 * is copied once into the child command; later parent changes cannot mutate
 * an already-created thread.
 */
export function prepareNestedWorldSessionExecutionBirth(input: {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly label: string;
  readonly parentId: string;
  readonly source: EnvironmentThreadShell | null;
  readonly modelSelection?: ModelSelection;
}): PreparedWorldSessionExecutionBirth | WorldSessionExecutionBirthRefusal {
  if (!input.worldKey.trim() || !input.sessionId.trim()) {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  if (!input.label.trim()) return { _tag: "Refused", reason: "empty-label" };
  const source = input.source;
  if (source === null || source.archivedAt !== null) {
    return { _tag: "Refused", reason: "source-execution-required" };
  }
  if (source.worldKey !== input.worldKey || source.sessionId !== input.parentId) {
    return { _tag: "Refused", reason: "source-coordinate-mismatch" };
  }
  const prepared = prepareRootSessionLaunch({
    mode: "world",
    activeWorldKey: input.worldKey,
    activeSessionId: input.sessionId,
    projectId: source.projectId,
    ask: input.label,
    now: new Date().toISOString(),
    modelSelection: input.modelSelection ?? source.modelSelection,
    runtimeMode: source.runtimeMode,
    interactionMode: source.interactionMode,
    branch: source.branch,
    worktreePath: source.worktreePath,
    parentThreadId: source.id,
    ...(source.workroomKey != null ? { workroomKey: source.workroomKey } : {}),
  });
  if (prepared._tag === "Refused") {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  return { _tag: "Launch", environmentId: source.environmentId, input: prepared.input };
}

/** The fields of a source execution a World fork copies; a shell or a detail thread. */
export type WorldForkSourceExecution = Pick<
  EnvironmentThreadShell,
  | "id"
  | "environmentId"
  | "projectId"
  | "title"
  | "worldKey"
  | "sessionId"
  | "workroomKey"
  | "archivedAt"
  | "modelSelection"
  | "runtimeMode"
  | "interactionMode"
  | "branch"
  | "worktreePath"
  | "latestTurn"
>;

/**
 * BD4: a fork copies the source execution's launch profile into a DIFFERENT
 * World Session. The World ledger carries forkedFrom; the local thread has no
 * parentThreadId because provenance is metadata rather than a thread edge.
 *
 * Can 46 (I4): with `throughMessageId`, the same `thread.create` also names
 * the source message, so the server copies everything said up to it into the
 * fork and owes its engine that conversation on the first Send, exactly as
 * for a Local fork. The fork starts on the source's current engine.
 */
export function prepareForkWorldSessionExecutionBirth(input: {
  readonly worldKey: string;
  readonly sourceSessionId: string;
  readonly forkSessionId: string;
  readonly label: string;
  readonly source: WorldForkSourceExecution | null;
  readonly throughMessageId?: MessageId | null;
}): PreparedWorldSessionExecutionBirth | WorldSessionExecutionBirthRefusal {
  if (
    !input.worldKey.trim() ||
    !input.sourceSessionId.trim() ||
    !input.forkSessionId.trim() ||
    input.sourceSessionId === input.forkSessionId
  ) {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  if (!input.label.trim()) return { _tag: "Refused", reason: "empty-label" };
  const source = input.source;
  if (source === null || source.archivedAt !== null) {
    return { _tag: "Refused", reason: "source-execution-required" };
  }
  if (source.worldKey !== input.worldKey || source.sessionId !== input.sourceSessionId) {
    return { _tag: "Refused", reason: "source-coordinate-mismatch" };
  }
  const prepared = prepareRootSessionLaunch({
    mode: "world",
    activeWorldKey: input.worldKey,
    activeSessionId: input.forkSessionId,
    projectId: source.projectId,
    ask: input.label,
    now: new Date().toISOString(),
    modelSelection: source.modelSelection,
    runtimeMode: source.runtimeMode,
    interactionMode: source.interactionMode,
    branch: source.branch,
    worktreePath: source.worktreePath,
    ...(source.workroomKey != null ? { workroomKey: source.workroomKey } : {}),
  });
  if (prepared._tag === "Refused") {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  return {
    _tag: "Launch",
    environmentId: source.environmentId,
    input:
      input.throughMessageId == null
        ? prepared.input
        : {
            ...prepared.input,
            forkedFrom: { threadId: source.id, throughMessageId: input.throughMessageId },
          },
  };
}

/**
 * SR-CAN-03: a continuation birth — a new thread on the SAME Session,
 * copying the standing execution's launch profile with no parentThreadId.
 * Fork has its own sibling-World-Session preparation above.
 */
export function prepareSiblingWorldSessionExecutionBirth(input: {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly label: string;
  readonly source: EnvironmentThreadShell | null;
}): PreparedWorldSessionExecutionBirth | WorldSessionExecutionBirthRefusal {
  if (!input.worldKey.trim() || !input.sessionId.trim()) {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  if (!input.label.trim()) return { _tag: "Refused", reason: "empty-label" };
  const source = input.source;
  if (source === null || source.archivedAt !== null) {
    return { _tag: "Refused", reason: "source-execution-required" };
  }
  if (source.worldKey !== input.worldKey || source.sessionId !== input.sessionId) {
    return { _tag: "Refused", reason: "source-coordinate-mismatch" };
  }
  const prepared = prepareRootSessionLaunch({
    mode: "world",
    activeWorldKey: input.worldKey,
    activeSessionId: input.sessionId,
    projectId: source.projectId,
    ask: input.label,
    now: new Date().toISOString(),
    modelSelection: source.modelSelection,
    runtimeMode: source.runtimeMode,
    interactionMode: source.interactionMode,
    branch: source.branch,
    worktreePath: source.worktreePath,
    ...(source.workroomKey != null ? { workroomKey: source.workroomKey } : {}),
  });
  if (prepared._tag === "Refused") {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  return {
    _tag: "Launch",
    environmentId: source.environmentId,
    input: {
      ...prepared.input,
      // A fresh execution stays in the same World session and therefore
      // keeps that session's existing parent. It does not become a new child
      // of the execution it replaces, or silently become a root execution.
      ...(source.parentThreadId == null ? {} : { parentThreadId: source.parentThreadId }),
    },
  };
}

/**
 * Stable once-per-mount key for the automatic base case. Null means the
 * World is not fully bound on this device and no dispatch is lawful.
 */
export function worldRootAutoSeatKey(input: {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly project: WorldSessionLaunchProject | null;
  readonly filesRoot: string | null;
}): string | null {
  if (
    !input.worldKey.trim() ||
    !input.sessionId.trim() ||
    input.project === null ||
    input.project.modelSelection === null ||
    input.filesRoot === null ||
    !input.filesRoot.trim()
  ) {
    return null;
  }
  return [
    input.worldKey,
    input.sessionId,
    input.project.environmentId,
    input.project.projectId,
    input.filesRoot,
  ].join("\u0000");
}

/**
 * Every member is a plain serializable discriminated union (JB6): closed
 * kinds, details as data fields, nothing that dies at an IPC/HTTP boundary.
 * The door's refusal kind is carried, never re-derived from prose (JB2).
 */
export type WorldSessionExecutionBirthOutcome =
  | WorldSessionExecutionBirthRefusal
  | {
      /**
       * The exact local identity could not be made durable, so the World
       * Session was deliberately not published. Without this gate a live
       * World subscription can mount the automatic launcher in the gap and
       * mint a competing execution for the same Session.
       */
      readonly _tag: "ReservationFailed";
      readonly reason: "birth-attempt-unavailable";
    }
  | {
      readonly _tag: "WorldFailed";
      /** The door's typed refusal kind — the only lawful switch surface. */
      readonly kind: WorldSessionDoorRefusalKind;
      /** An uncertain write must retain its original request and identity. */
      readonly retryable: boolean;
      /** Human sentence carried as detail; its wording is never load-bearing. */
      readonly reason: string;
    }
  | {
      readonly _tag: "Complete";
      readonly entry: WorldRoomEntry;
      readonly environmentId: EnvironmentId;
      readonly threadId: ThreadId;
    }
  | {
      readonly _tag: "Partial";
      readonly entry: WorldRoomEntry;
      readonly environmentId: EnvironmentId;
      readonly threadId: ThreadId;
      readonly reason: "execution-unconfirmed" | "context-unconfirmed";
      readonly detail?: string;
    }
  | {
      /**
       * The thread-create itself failed (JB5): the World Session exists but the
       * local dispatch reported failure — distinct from Partial, whose
       * dispatch was accepted and merely unobserved. The prepared identity
       * stays retryable either way.
       */
      readonly _tag: "DispatchFailed";
      readonly entry: WorldRoomEntry;
      readonly environmentId: EnvironmentId;
      readonly threadId: ThreadId;
      readonly reason: "thread-create-failed";
    };

interface OpenedSessionBaseInput {
  readonly modelSelection?: ModelSelection;
  readonly worldKey: string;
  readonly sessionId: string;
  readonly label: string;
  /** Explicit exact-goal binding; absent means the authority's inheritance law. */
  readonly goalId?: string;
  readonly goalIds?: ReadonlyArray<string>;
  readonly assignment?: string;
  readonly contextBirth?: WorldContextBirthClaim;
}

type OpenedSessionInput =
  | (OpenedSessionBaseInput & {
      readonly birthSource?: "world";
      /** A ROOT session's parent is its room (R_): the World never parents a session directly. */
      readonly parentId: string;
      readonly project: WorldSessionLaunchProject | null;
      readonly filesRoot: string | null;
      /** SB3: the launching workroom; carried into the prepared command. */
      readonly workroomKey?: string | null;
    })
  | (OpenedSessionBaseInput & {
      readonly birthSource?: "parent";
      readonly parentId: string;
      readonly source: EnvironmentThreadShell | null;
    });

/**
 * One deliberately non-atomic cross-authority birth. The exact execution
 * identity is reserved first, World Session readback is second, and local
 * execution creation is third. A local dispatch failure retains the
 * authoritative Session and the same prepared thread identity for retry.
 */
export function createWorldSessionExecutionLauncher(deps: {
  readonly openSession: (input: OpenedSessionInput) => Promise<WorldRoomEntryOutcome>;
  readonly dispatchExecution: (
    environmentId: EnvironmentId,
    input: CreateThreadInput,
  ) => Promise<boolean>;
  readonly executionArrived: (environmentId: EnvironmentId, threadId: ThreadId) => Promise<boolean>;
  /**
   * JB1: persist the attempt's identity pair as a durable server-side intent
   * before publishing the World Session. The automatic first-execution rail
   * can therefore adopt this identity as soon as the Session is observable.
   */
  readonly recordAttempt?: (attempt: {
    readonly worldKey: string;
    readonly sessionId: string;
    readonly environmentId: EnvironmentId;
    readonly input: CreateThreadInput;
  }) => Promise<{ readonly ok: boolean }>;
  /** BD6: root births seat their board Context before provider dispatch. */
  readonly initializeContext?: (input: {
    readonly worldKey: string;
    readonly roomId: string;
    readonly sessionId: string;
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
    readonly boardKey?: string | null;
  }) => Promise<{ readonly ok: boolean; readonly reason?: string }>;
}) {
  let inFlight: Promise<WorldSessionExecutionBirthOutcome> | null = null;
  const coalesce = (run: () => Promise<WorldSessionExecutionBirthOutcome>) => {
    if (inFlight !== null) return inFlight;
    inFlight = run().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };
  let pendingWorld: {
    readonly input: OpenedSessionInput;
    readonly prepared: PreparedWorldSessionExecutionBirth;
    uncertain: boolean;
  } | null = null;
  let retryable: {
    readonly prepared: PreparedWorldSessionExecutionBirth;
    readonly entry: WorldRoomEntry;
    readonly initializeContext?: () => Promise<{ readonly ok: boolean; readonly reason?: string }>;
  } | null = null;

  const dispatch = async (
    prepared: PreparedWorldSessionExecutionBirth,
    entry: WorldRoomEntry,
    initializeContext?: () => Promise<{ readonly ok: boolean; readonly reason?: string }>,
  ): Promise<WorldSessionExecutionBirthOutcome> => {
    const dispatched = await deps.dispatchExecution(prepared.environmentId, prepared.input);
    // A successful dispatch resolves only after thread.create commits, which
    // is the ownership fact the Context door authenticates. Initialize now,
    // before the UI's detail-atom readback: the World Session shell may adopt
    // the new thread while that atom is still cold and unmount its launcher.
    let initialized =
      dispatched && initializeContext !== undefined ? await initializeContext() : null;
    // Arrival is checked even when the dispatch reports failure: a
    // committed-but-ack-lost create fails its re-dispatch on
    // require-thread-absent while the thread exists — reconciliation by
    // authoritative readback settles it as Complete (the rootSessionBirth
    // law), so exactly-once holds without trusting either signal alone.
    const arrived = await deps.executionArrived(prepared.environmentId, prepared.input.threadId);
    if (arrived) {
      // A committed-but-ack-lost dispatch proves ownership only at this
      // readback, so its Context initialization belongs here.
      if (!dispatched && initializeContext !== undefined) {
        initialized = await initializeContext();
      }
      if (initialized !== null) {
        if (!initialized.ok) {
          retryable = {
            prepared,
            entry,
            ...(initializeContext === undefined ? {} : { initializeContext }),
          };
          return {
            _tag: "Partial",
            entry,
            environmentId: prepared.environmentId,
            threadId: prepared.input.threadId,
            reason: "context-unconfirmed",
            ...(initialized.reason === undefined ? {} : { detail: initialized.reason }),
          };
        }
      }
      retryable = null;
      return {
        _tag: "Complete",
        entry,
        environmentId: prepared.environmentId,
        threadId: prepared.input.threadId,
      };
    }
    retryable = {
      prepared,
      entry,
      ...(initializeContext === undefined ? {} : { initializeContext }),
    };
    if (!dispatched) {
      return {
        _tag: "DispatchFailed",
        entry,
        environmentId: prepared.environmentId,
        threadId: prepared.input.threadId,
        reason: "thread-create-failed",
      };
    }
    return {
      _tag: "Partial",
      entry,
      environmentId: prepared.environmentId,
      threadId: prepared.input.threadId,
      reason: "execution-unconfirmed",
    };
  };

  const openPrepared = async (
    candidateInput: OpenedSessionInput,
    candidatePrepared: PreparedWorldSessionExecutionBirth,
  ): Promise<WorldSessionExecutionBirthOutcome> => {
    // A failed readback does not prove the World write failed. Keep both
    // identities and the chosen model until that same birth is confirmed.
    pendingWorld ??= { input: candidateInput, prepared: candidatePrepared, uncertain: false };
    const batch = pendingWorld;
    const { input, prepared } = batch;
    let opened: Awaited<ReturnType<typeof deps.openSession>>;
    try {
      opened = await deps.openSession(batch.input);
    } catch {
      batch.uncertain = true;
      return {
        _tag: "WorldFailed",
        kind: "transport",
        reason: "The session creation could not be confirmed. Retry the same creation.",
        retryable: true,
      };
    }
    if (!opened.ok) {
      if (opened.kind === "context_changed" && !batch.uncertain) pendingWorld = null;
      else batch.uncertain = true;
      return {
        _tag: "WorldFailed",
        kind: normalizeWorldSessionDoorRefusalKind(opened.kind),
        retryable: pendingWorld !== null,
        reason: opened.reason,
      };
    }
    pendingWorld = null;
    const initializeContext =
      ROOM_ID_PATTERN.test(input.parentId) && deps.initializeContext !== undefined
        ? () =>
            deps.initializeContext!({
              worldKey: input.worldKey,
              roomId: input.parentId,
              sessionId: input.sessionId,
              environmentId: prepared.environmentId,
              threadId: prepared.input.threadId,
              ...("workroomKey" in input ? { boardKey: input.workroomKey } : {}),
            })
        : undefined;
    return dispatch(prepared, opened.entry, initializeContext);
  };

  return {
    openAndLaunch: async (input: OpenedSessionInput): Promise<WorldSessionExecutionBirthOutcome> =>
      coalesce(async () => {
        if (pendingWorld !== null) {
          if (pendingWorld.input.worldKey !== input.worldKey)
            return { _tag: "Refused", reason: "source-coordinate-mismatch" };
          return openPrepared(pendingWorld.input, pendingWorld.prepared);
        }
        const rootBirth = ROOM_ID_PATTERN.test(input.parentId);
        const prepared = rootBirth
          ? prepareWorldRootSessionExecutionBirth(
              input as Extract<OpenedSessionInput, { readonly project: unknown }>,
            )
          : prepareNestedWorldSessionExecutionBirth(
              input as Extract<OpenedSessionInput, { readonly source: unknown }>,
            );
        if (prepared._tag === "Refused") return prepared;
        if (deps.recordAttempt !== undefined) {
          try {
            const reserved = await deps.recordAttempt({
              worldKey: input.worldKey,
              sessionId: input.sessionId,
              environmentId: prepared.environmentId,
              input: prepared.input,
            });
            if (!reserved.ok) {
              return { _tag: "ReservationFailed", reason: "birth-attempt-unavailable" };
            }
          } catch {
            return { _tag: "ReservationFailed", reason: "birth-attempt-unavailable" };
          }
        }
        // The governed Context door proves the local thread owns this World
        // Session. Therefore the blank thread shell must exist before Context
        // initialization. thread.create does not start a provider turn; the
        // first turn remains gated on this exact read/move/readback sequence.
        return openPrepared(input, prepared);
      }),
    retryPartial: async (): Promise<
      | WorldSessionExecutionBirthOutcome
      | { readonly _tag: "Refused"; readonly reason: "no-retryable-launch" }
    > => {
      if (inFlight !== null) return inFlight;
      if (pendingWorld !== null) {
        const batch = pendingWorld;
        return coalesce(() => openPrepared(batch.input, batch.prepared));
      }
      if (retryable === null) return { _tag: "Refused", reason: "no-retryable-launch" };
      const batch = retryable;
      return coalesce(() => dispatch(batch.prepared, batch.entry, batch.initializeContext));
    },
  };
}
